import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import logger from '@achieve/cx-logger'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import {
  UNLICENSED_ERROR,
  DEFAULT_PAYMENTS,
  DEFAULT_SLIDER_CONFIG,
  roundDownToNearestThousand,
} from './utils'

function useHELOCCalculator({ helocOutput, setEquityError, recalculate, jsonConfig }) {
  const { fireAndForget } = useTriggerEvents()
  const [isLoading, setIsLoading] = useState(true)
  const [licensedFlag, setLicensedFlag] = useState(true)
  const [sliderAmount] = useState(
    helocOutput?.loan_amount > 0 ? helocOutput?.loan_amount : DEFAULT_SLIDER_CONFIG.min
  )
  const [term, setTerm] = useState(jsonConfig?.termInitialValue || 2)
  const [customAPR, setCustomAPR] = useState(jsonConfig?.defaultCustomAPR || 8)
  const [loanPayments, setLoanPayments] = useState(
    jsonConfig?.loanPaymentsDefault || DEFAULT_PAYMENTS
  )
  const [modalStatus, setModalStatus] = useState(false)
  const [sliderAmountConfig, setSliderAmountConfig] = useState(
    jsonConfig?.sliderAmountConfig || DEFAULT_SLIDER_CONFIG
  )
  const apiUrl = jsonConfig?.apiUrl || '/api/achieve-tools/heloc'
  const [debouncedSliderAmount, setDebouncedSliderAmount] = useState(
    roundDownToNearestThousand(sliderAmount, sliderAmountConfig.max)
  )
  const [negativeEquity, setNegativeEquity] = useState(false)
  const [negativeEquityConfig] = useState(jsonConfig?.negativeEquity)

  const latestRef = useRef({})
  latestRef.current = {
    loanPayments,
    term,
    licensedFlag,
    customAPR,
    helocOutput,
    setEquityError,
    fireAndForget,
    debouncedSliderAmount,
    apiUrl,
  }

  const initialMinRef = useRef(sliderAmountConfig.min)

  const apiCall = useCallback(async (loan_amount) => {
    const {
      loanPayments: currentLoanPayments,
      term: currentTerm,
      licensedFlag: currentLicensedFlag,
      customAPR: currentCustomAPR,
      helocOutput: currentHelocOutput,
      setEquityError: currentSetEquityError,
      fireAndForget: currentFireAndForget,
      debouncedSliderAmount: currentDebouncedSliderAmount,
      apiUrl: currentApiUrl,
    } = latestRef.current

    const actualLoanAmount = loan_amount ?? currentDebouncedSliderAmount

    let syncEquitySucces = true
    let successLoanLicense = true
    try {
      setIsLoading(true)
      setNegativeEquity(false)
      currentSetEquityError(false)
      const data = {
        ...currentHelocOutput,
        loan_amount: Number(actualLoanAmount),
        term: currentLoanPayments[currentTerm - 1]?.term,
        ...(!currentLicensedFlag ? { apr: currentCustomAPR } : null),
      }

      const JSONdata = JSON.stringify(data)
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSONdata,
      }

      const response = await fetch(currentApiUrl, options)

      let result = await response.json()

      if (result?.loan_payment_details?.is_equity_below_threshold) {
        setNegativeEquity(true)
        currentSetEquityError(true)
        syncEquitySucces = false
      }
      if (result?.loan_payment_details?.max_loan_amount > 0) {
        const eligibleMax = Math.round(result?.loan_payment_details?.max_loan_amount)
        const desiredLoanAmount = Number(currentHelocOutput?.loan_amount) || 0
        setSliderAmountConfig((prev) => ({
          ...prev,
          max: Math.max(eligibleMax, desiredLoanAmount),
        }))
      }

      if (result?.loan_payment_details?.loan_payments?.length > 0) {
        setLoanPayments(result?.loan_payment_details?.loan_payments)
      }

      setLicensedFlag(result?.error_message !== UNLICENSED_ERROR)
      successLoanLicense = result?.error_message !== UNLICENSED_ERROR

      if (!result?.loan_payment_details?.loan_payments[currentTerm - 1]) {
        setTerm(result?.loan_payment_details?.loan_payments.length || 2)
      }
    } catch (error) {
      logger.error('HELOC calculator request failed', { error })
    } finally {
      if (syncEquitySucces && successLoanLicense) {
        currentFireAndForget({ event_type: 'heloc_calc_eligible_loan' })
      }
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    setDebouncedSliderAmount(initialMinRef.current)
  }, [])

  useEffect(() => {
    setDebouncedSliderAmount(helocOutput?.loan_amount)
  }, [helocOutput, licensedFlag])

  useEffect(() => {
    apiCall()
  }, [debouncedSliderAmount, recalculate, apiCall])

  const chartData = useMemo(
    () =>
      loanPayments?.map((e, pos) => ({
        year: `${e?.term} yrs`,
        amount: e?.monthly_payment,
        label: `$${e?.monthly_payment}/mo`,
        isHighlighted: pos === term - 1,
      })),
    [loanPayments, term]
  )

  const handleReleaseAmount = useCallback((amount) => {
    setDebouncedSliderAmount(amount)
  }, [])

  const handleChangeTerm = useCallback((e) => {
    setTerm(e.target.value)
  }, [])

  const handleChangeCustomAPR = useCallback((e) => {
    setCustomAPR(e.target.value)
  }, [])

  const finalTitle =
    !licensedFlag && jsonConfig?.NonNegativeEquity
      ? jsonConfig?.NonNegativeEquity?.unlicenseState?.headline
      : jsonConfig?.NonNegativeEquity?.licenseState?.headline

  const finalSubTitle =
    !licensedFlag && jsonConfig?.NonNegativeEquity
      ? jsonConfig?.NonNegativeEquity?.unlicenseState?.subheadline
      : jsonConfig?.NonNegativeEquity?.licenseState?.subheadline

  const negativeEquityTitleSubtitle = !licensedFlag
    ? negativeEquityConfig?.licenseUnlicense?.unlicenseState
    : negativeEquityConfig?.licenseUnlicense?.licenseState

  return {
    isLoading,
    licensedFlag,
    term,
    customAPR,
    loanPayments,
    modalStatus,
    setModalStatus,
    sliderAmountConfig,
    debouncedSliderAmount,
    negativeEquity,
    negativeEquityConfig,
    chartData,
    finalTitle,
    finalSubTitle,
    negativeEquityTitleSubtitle,
    handleReleaseAmount,
    handleChangeTerm,
    handleChangeCustomAPR,
  }
}

export { useHELOCCalculator }
